import { useState, useEffect, useCallback } from 'react'
import { MainLayout } from '../../components/layout/MainLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Plus, Search, Edit2, Trash2, Check, X, ChevronLeft, ChevronRight, Shield } from 'lucide-react'
import { usuarioService } from '../../services/usuarioService'
import type { Usuario } from '../../services/usuarioService'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from '../../utils/toast'
import { TableSkeleton } from '../../components/ui/Skeleton'

const usuarioSchema = z.object({
  name: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
  email: z.string().email('E-mail inválido'),
  password: z.string().min(4, 'Senha deve ter no mínimo 4 caracteres').optional().or(z.literal('')),
  role: z.enum(['admin', 'vendedor', 'operador'], {
    message: 'Selecione uma função válida'
  }),
})

type UsuarioFormData = z.infer<typeof usuarioSchema>

export function UsuariosPage() {
  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [loading, setLoading] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingUsuario, setEditingUsuario] = useState<Usuario | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'vendedor' | 'operador'>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | 'ativo' | 'inativo'>('all')

  // Paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const { register, handleSubmit, reset, formState: { errors } } = useForm<UsuarioFormData>({
    resolver: zodResolver(usuarioSchema),
  })

  const loadUsuarios = useCallback(async () => {
    setLoading(true)
    try {
      const result = await usuarioService.getAll({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        role: roleFilter === 'all' ? undefined : roleFilter,
        status: statusFilter === 'all' ? undefined : statusFilter,
        sort: 'name:asc'
      })

      setUsuarios(result.data)
      setTotalPages(result.pagination.totalPages)
      setTotalItems(result.pagination.total)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao carregar usuários'
      toast.error(message)
      console.error('Erro ao carregar usuários:', error)
    } finally {
      setLoading(false)
      setIsInitialLoad(false)
    }
  }, [currentPage, itemsPerPage, searchTerm, roleFilter, statusFilter])

  useEffect(() => {
    if (isInitialLoad) {
      loadUsuarios()
    }
  }, [isInitialLoad, loadUsuarios])

  // Recarrega quando filtros/paginação mudam (após load inicial)
  useEffect(() => {
    if (!isInitialLoad) {
      loadUsuarios()
    }
  }, [currentPage, roleFilter, statusFilter])

  // Debounce para busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1)
      } else {
        loadUsuarios()
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchTerm, currentPage, loadUsuarios])

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Administrador',
      vendedor: 'Vendedor',
      operador: 'Operador',
    }
    return labels[role] || role
  }

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400',
      vendedor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
      operador: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
    }
    return colors[role] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
  }

  const openModal = (usuario?: Usuario) => {
    if (usuario) {
      setEditingUsuario(usuario)
      reset({
        name: usuario.name,
        email: usuario.email,
        role: usuario.role,
        password: '',
      })
    } else {
      setEditingUsuario(null)
      reset({ name: '', email: '', password: '', role: 'vendedor' })
    }
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingUsuario(null)
    reset()
  }

  const onSubmit = async (data: UsuarioFormData) => {
    try {
      if (editingUsuario) {
        // Atualizar usuário
        const updateData: any = {
          name: data.name,
          email: data.email,
          role: data.role,
        }
        
        const senhaAlterada = data.password && data.password.length > 0
        
        // Se senha foi preenchida, adiciona ao payload
        if (senhaAlterada) {
          updateData.password = data.password
          console.log('Atualizando usuário COM nova senha')
        } else {
          console.log('Atualizando usuário SEM alterar senha')
        }
        
        await usuarioService.update(editingUsuario.id, updateData)
        
        if (senhaAlterada) {
          toast.success('Usuário e senha atualizados com sucesso!')
        } else {
          toast.success('Usuário atualizado com sucesso!')
        }
      } else {
        // Criar usuário
        if (!data.password) {
          toast.error('Senha é obrigatória para novo usuário')
          return
        }
        await usuarioService.create({
          name: data.name,
          email: data.email,
          password: data.password,
          role: data.role,
        })
        toast.success('Usuário criado com sucesso!')
      }

      await loadUsuarios()
      closeModal()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao salvar usuário'
      toast.error(message)
      console.error('Erro ao salvar usuário:', error)
    }
  }

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await usuarioService.delete(id)
        await loadUsuarios()
        toast.success('Usuário excluído com sucesso!')
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erro ao excluir usuário'
        toast.error(message)
        console.error('Erro ao excluir usuário:', error)
      }
    }
  }

  const toggleStatus = async (usuario: Usuario) => {
    try {
      await usuarioService.update(usuario.id, {
        ativo: !usuario.ativo
      })
      await loadUsuarios()
      toast.success(`Usuário ${usuario.ativo ? 'desativado' : 'ativado'} com sucesso!`)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao alterar status'
      toast.error(message)
      console.error('Erro ao alterar status:', error)
    }
  }

  return (
    <MainLayout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-purple-600 dark:text-purple-400" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Gerenciar Usuários
            </h1>
          </div>
          <Button onClick={() => openModal()}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Usuário
          </Button>
        </div>

        <Card>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div>
                <select
                  value={roleFilter}
                  onChange={(e) => {
                    setRoleFilter(e.target.value as any)
                    setCurrentPage(1)
                  }}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">Todas as funções</option>
                  <option value="admin">Administrador</option>
                  <option value="vendedor">Vendedor</option>
                  <option value="operador">Operador</option>
                </select>
              </div>

              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as any)
                    setCurrentPage(1)
                  }}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="all">Todos os status</option>
                  <option value="ativo">Ativos</option>
                  <option value="inativo">Inativos</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Mostrando {usuarios.length} de {totalItems} usuários
              </p>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-gray-400">Itens por página:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          {loading && isInitialLoad ? (
            <TableSkeleton />
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto animate-fadeIn" style={{ minHeight: '200px' }}>
                <table className="w-full">
                  <thead className="border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Nome
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Email
                      </th>
                      <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                        Função
                      </th>
                      <th className="pb-3 text-center text-sm font-semibold text-gray-900 dark:text-white">
                        Status
                      </th>
                      <th className="pb-3 text-right text-sm font-semibold text-gray-900 dark:text-white">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {usuarios.map((usuario) => (
                      <tr
                        key={usuario.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <td className="py-4 text-sm font-medium text-gray-900 dark:text-white">
                          {usuario.name}
                        </td>
                        <td className="py-4 text-sm text-gray-700 dark:text-gray-300">
                          {usuario.email}
                        </td>
                        <td className="py-4">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getRoleColor(usuario.role)}`}>
                            {getRoleLabel(usuario.role)}
                          </span>
                        </td>
                        <td className="py-4 text-center">
                          <button
                            onClick={() => toggleStatus(usuario)}
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                              usuario.ativo
                                ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-400 dark:hover:bg-green-900/60'
                                : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-400 dark:hover:bg-red-900/60'
                            }`}
                          >
                            {usuario.ativo ? (
                              <>
                                <Check className="mr-1 h-3 w-3" />
                                Ativo
                              </>
                            ) : (
                              <>
                                <X className="mr-1 h-3 w-3" />
                                Inativo
                              </>
                            )}
                          </button>
                        </td>
                        <td className="py-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openModal(usuario)}
                              title="Editar usuário"
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(usuario.id)}
                              title="Excluir usuário"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Paginação Desktop */}
                {totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Página {currentPage} de {totalPages}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Próxima
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-4 animate-fadeIn mt-6">
                {usuarios.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-gray-500 dark:text-gray-400">
                      Nenhum usuário encontrado
                    </p>
                  </div>
                ) : (
                  usuarios.map((usuario) => (
                    <div
                      key={usuario.id}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {usuario.name}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {usuario.email}
                          </p>
                        </div>
                        <button
                          onClick={() => toggleStatus(usuario)}
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                            usuario.ativo
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                          }`}
                        >
                          {usuario.ativo ? (
                            <>
                              <Check className="h-3 w-3" />
                            </>
                          ) : (
                            <>
                              <X className="h-3 w-3" />
                            </>
                          )}
                        </button>
                      </div>

                      <div className="mb-4">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${getRoleColor(usuario.role)}`}>
                          <Shield className="h-3 w-3 mr-1" />
                          {getRoleLabel(usuario.role)}
                        </span>
                      </div>

                      <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => openModal(usuario)}
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(usuario.id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}

                {/* Paginação Mobile */}
                {totalPages > 1 && (
                  <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Página {currentPage} de {totalPages}
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 sm:flex-initial"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 sm:flex-initial"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Próxima
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Modal de Criar/Editar */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingUsuario ? 'Editar Usuário' : 'Novo Usuário'}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Nome *
            </label>
            <Input
              {...register('name')}
              placeholder="Nome completo"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors.name.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Email *
            </label>
            <Input
              {...register('email')}
              type="email"
              placeholder="email@exemplo.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Senha {editingUsuario ? '(opcional - deixe em branco para manter a atual)' : '*'}
            </label>
            <Input
              {...register('password')}
              type="password"
              placeholder={editingUsuario ? 'Digite nova senha para alterar' : 'Senha (mín. 6 caracteres)'}
              autoComplete={editingUsuario ? 'new-password' : 'off'}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors.password.message}
              </p>
            )}
            {editingUsuario && (
              <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                💡 Preencha apenas se quiser alterar a senha deste usuário
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">
              Função *
            </label>
            <select
              {...register('role')}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            >
              <option value="vendedor">Vendedor</option>
              <option value="operador">Operador</option>
              <option value="admin">Administrador</option>
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors.role.message}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={closeModal}>
              Cancelar
            </Button>
            <Button type="submit">
              {editingUsuario ? 'Salvar Alterações' : 'Criar Usuário'}
            </Button>
          </div>
        </form>
      </Modal>
    </MainLayout>
  )
}
